import {computed, makeObservable, observable} from "mobx";
import {BaseModuleState} from "../../../infrastructure/models/base-module-state";
import {DASHBOARD} from "../../../containers/dashboard-container/dashboard-container.consts";
import {AllScoresLinkModel} from "../../../containers/all-scores-container/models/all-scores-container-link-model";
import {PAGES, ROUTE_NAMES} from "../../../core/consts";
import {ListItemViewModel} from "../../../infrastructure/models/list-item.view-model";
import {BaseMobxState} from "../../../infrastructure/mobx/base-mobx-state";
import {NavigationManager} from "../../../core/managers/navigation-manager";
import {AppStore} from "../../../core/app/app-store";
import {LoggerUtils} from "../../../infrastructure/utils/logger/logger-utils";
import {DatetimeUtils} from "../../../infrastructure/utils/datetime-utils";
import {DeviceViewModel} from "../../../core/models/device.view-model";
import {TermsUtils} from "../../../infrastructure/utils/terms-utils";
import {ScoresBarWidgetConfig} from "../scores-bar-widget.config";
import {BaseLinkModel} from "../../../infrastructure/models/link/base-link-model";
import {SCORES_BAR} from "../scores-bar-widget.consts";
import {ScoresBarWidgetScoresModel} from "./scores/scores-bar-widget.scores.model";
import {ScoresBarWidgetAllScoresModel} from "./scores/scores-bar-widget.all-scores.model";
import {ScoresBarWidgetMyScoresModel} from "./scores/scores-bar-widget.my-scores.model";
import {ScoresBarWidgetCompetitionScoresModel} from "./scores/scores-bar-widget.competition-scores.model";
import {ScoresBarWidgetCompetitorScoresModel} from "./scores/scores-bar-widget.competitor-scores.model";
import {EntityContainerHashParamsModel} from "../../../core/components/router/models/entity-container-hash-params-model";
import {NEW_COMPETITOR_CONTAINER} from "../../../containers/competitor-container/competitor-container.consts";
import {ScoresBarWidgetTerms} from "../scores-bar-widget.terms";
import {ENTITY_SCORES} from "../../entity-scores-widget/entity-scores-widget.consts";
import {ScoresBarWidgetAthleteScoresModel} from "./scores/scores-bar-widget.athlete-scores.model";
import {makeObservableClass} from "../../../infrastructure/decorators/observable";
import {WebsiteModuleState} from "../../../apps/website/models/website-module.state";
@makeObservableClass
export class ScoresBarWidgetState extends BaseModuleState {
  @observable currentTabIndex: Number;
  @observable filter: String;
  @observable _isLive: Boolean;
  @observable currentContent: ScoresBarWidgetScoresModel;
  terms: ScoresBarWidgetTerms;
  config: ScoresBarWidgetConfig;

  static injectables = [...BaseMobxState.injectables, NavigationManager, AppStore, LoggerUtils, DatetimeUtils, DeviceViewModel, TermsUtils, ScoresBarWidgetConfig, WebsiteModuleState];
  constructor(
      navigationManager: NavigationManager,
      appStore: AppStore,
      logger: LoggerUtils,
      datetimeUtils: DatetimeUtils,
      device: DeviceViewModel,
      termsUtils: TermsUtils,
      config: ScoresBarWidgetConfig,
      websiteModuleState: WebsiteModuleState,
  ) {
    super(navigationManager, appStore, logger, datetimeUtils, device, termsUtils, config);

    this.websiteModuleState = websiteModuleState;

    makeObservable(this)
  }
  get isPopulated() {
    return true;
  }

  @computed get allScoresTab(): ScoresBarWidgetAllScoresModel {
    const validRoutes = [ROUTE_NAMES.allScores, ROUTE_NAMES.myScoresPage];

    const allScoresTab = new ListItemViewModel({
      name: this.terms.ALL_FOOTBALL_SCORES,
      linkModel: new AllScoresLinkModel(DASHBOARD.FILTERS.all, this.sportType?.nameForURL),
      visible: validRoutes.includes(this.currentLinkModel?.name),
      index: SCORES_BAR.SCORES_INDEXES.allScores
    });

    return allScoresTab;
  }

  @computed get myScoresTab(): ScoresBarWidgetMyScoresModel {
    const myScoresTab = new ListItemViewModel({
      name: this.terms.WEB_MY_SCORES_TITLE,
      linkModel: null,
      visible: true,
      index: SCORES_BAR.SCORES_INDEXES.myScores,
      shouldRenderLink: this.currentLinkModel?.name === ROUTE_NAMES.myScoresPage
    });

    return myScoresTab;
  }

  @computed get competitionTab(): ScoresBarWidgetCompetitionScoresModel {
    const validRoutes = [
      ROUTE_NAMES.competitionScores,
      ROUTE_NAMES.competitionStandings,
      ROUTE_NAMES.competitionBrackets,
      ROUTE_NAMES.game
    ];

    const competitionTab = new ListItemViewModel({
      name: this.terms.MOBILE_MENU_SCORES,
      linkModel: null,
      visible: validRoutes.includes(this.currentLinkModel.name),
      index: SCORES_BAR.SCORES_INDEXES.competitionScores
    });

    return competitionTab;
  }

  @computed get competitorTab(): ScoresBarWidgetCompetitorScoresModel {
    const validRoutes = [ROUTE_NAMES.competitorScores, ROUTE_NAMES.competitorStandings, ROUTE_NAMES.competitorBrackets];

    const competitorTab = new ListItemViewModel({
      name: this.terms.MOBILE_MENU_SCORES,
      linkModel: null,
      visible: validRoutes.includes(this.currentLinkModel.name),
      index: SCORES_BAR.SCORES_INDEXES.competitorScores
    });

    return competitorTab;
  }

  @computed get athleteTab(): ScoresBarWidgetAthleteScoresModel {
    const validRoutes = [ROUTE_NAMES.squad, ROUTE_NAMES.athlete];

    const athleteTab = new ListItemViewModel({
      name: this.terms.MOBILE_MENU_SCORES,
      linkModel: null,
      visible: validRoutes.includes(this.currentLinkModel.name),
      index: this.context
    });

    return athleteTab;
  }

  @computed get showOdds(): Boolean {
    return this.appState.showOdds;
  }

  @computed get isToday() {
    return this.datetimeUtils.isToday(this.currentContent?.date)
  }

  @computed get isAllScores(): Boolean {
    return this.currentTabIndex === SCORES_BAR.SCORES_INDEXES.allScores;
  }

  @computed get isMyScores(): Boolean {
    return this.currentTabIndex === SCORES_BAR.SCORES_INDEXES.myScores;
  }

  @computed get isCompetitionScores(): Boolean {
    return this.currentTabIndex === SCORES_BAR.SCORES_INDEXES.competitionScores;
  }

  @computed get isCompetitorScores(): Boolean {
    return this.currentTabIndex === SCORES_BAR.SCORES_INDEXES.competitorScores;
  }

  @computed get isAthleteContext(): Boolean {
    return this.currentTabIndex === SCORES_BAR.SCORES_INDEXES.athlete;
  }

  @computed get shouldRenderHeader(): Boolean {
    return !this.isAthleteContext;
  }

  get liveScoresLinkModel(): BaseLinkModel {
    return new AllScoresLinkModel(DASHBOARD.FILTERS.live, this.appState.context.sportType?.nameForURL);
  }

  @computed get isMyScoresPage() {
    return this.currentLinkModel?.name === ROUTE_NAMES.myScoresPage
  }

  @computed get visibleTabs(): Array<ListItemViewModel> {
    const tabs: Array<ListItemViewModel>= [this.allScoresTab, this.competitionTab, this.competitorTab, this.myScoresTab, this.athleteTab]
    const visibleTabs: Array<ListItemViewModel> = tabs.map(tab => {
      // const myScoresContext = tab.index === SCORES_BAR.SCORES_INDEXES.myScores && this.isMyScoresPage;

      tab.active = tab.index === this.currentTabIndex;// || myScoresContext;

      return tab;
    }).filter(tab => tab.visible);

    return visibleTabs;
  }

  @computed get isLive(): Boolean {
    let live: Boolean = this._isLive;

    if (this.currentTabIndex === SCORES_BAR.SCORES_INDEXES.allScores) {
      live = this.currentLinkModel?.params?.filter === DASHBOARD.FILTERS.live
    }

    return live;
  }

  itemHashParams(urlFilter): EntityContainerHashParamsModel {
    let hashParams = new EntityContainerHashParamsModel({[urlFilter]: urlFilter})

    if (urlFilter === NEW_COMPETITOR_CONTAINER.FILTERS.recent) {
      hashParams = new EntityContainerHashParamsModel({recent: ''})
    }

    return hashParams;
  }

  setActiveItem(item) {
    const currentHash = this.currentLinkModel?.hashParams.toString()
    const filters = ENTITY_SCORES.FILTERS_ARRAY.map(filter => filter.urlFilter)
    const currentFilter = currentHash.replace('#', '')

    if (filters.includes(currentFilter)) {
      item.active = currentHash === item.linkModel?.hash
    } else {

      item.active = item.id === 0
    }
  }

  @computed get sportType(): Number {
    return this.appState.context.sportType;
  }

  @computed get showOddsButton(): Number {
    return this.appState.topBookmaker?.id > 0;
  }

  @computed get competitorSquadId(): Number {
    const competitorSquadId: Number = this.entitiesContext.competition?.id
    return competitorSquadId
  }

  createContent(contextType: SCORES_BAR.SCORES_INDEXES): ScoresBarWidgetScoresModel {
    let content: ScoresBarWidgetScoresModel;

    if (contextType === SCORES_BAR.SCORES_INDEXES.allScores) {
      content = new ScoresBarWidgetAllScoresModel(SCORES_BAR.SCORES_INDEXES.allScores, this.currentLinkModel, this.terms, this.datetimeUtils, this.sportType, this.appState, this.config);
    } else if (contextType === SCORES_BAR.SCORES_INDEXES.myScores) {
      content = new ScoresBarWidgetMyScoresModel(SCORES_BAR.SCORES_INDEXES.myScores, this.currentLinkModel, this.terms, this.datetimeUtils);
    } else if (contextType === SCORES_BAR.SCORES_INDEXES.competitorScores) {
      content = new ScoresBarWidgetCompetitorScoresModel(SCORES_BAR.SCORES_INDEXES.competitorScores, this.currentLinkModel, this.terms, this.datetimeUtils);
    } else if (contextType === SCORES_BAR.SCORES_INDEXES.athlete) {
      content = new ScoresBarWidgetAthleteScoresModel(SCORES_BAR.SCORES_INDEXES.athlete, this.currentLinkModel, this.terms, this.datetimeUtils);
    } else {
      content = new ScoresBarWidgetCompetitionScoresModel(SCORES_BAR.SCORES_INDEXES.competitionScores, this.currentLinkModel, this.terms, this.datetimeUtils);
    }

    return content;
  }

  @computed get shouldRenderRelatedEntities(): boolean {
    return this.isCompetitionScores || this.isCompetitorScores || this.isAthleteContext;
  }

  @computed get relatedEntitiesProps() {
    let relatedEntitiesProps = {};
    if (this.isCompetitionScores)
      relatedEntitiesProps = {competitionId: this.websiteModuleState.entitiesContext?.competition?.id};
    else if(this.isCompetitorScores){
      relatedEntitiesProps = {competitorId: this.websiteModuleState.entitiesContext?.competitor?.id};
    }
    else if(this.isAthleteContext){
      relatedEntitiesProps = {athleteId: this.websiteModuleState.entitiesContext?.athlete?.id};
    }

    return relatedEntitiesProps;
  }

  @computed get isBettingPage(): Boolean {
    const bettingPages: Array<ROUTE_NAMES> = [ROUTE_NAMES.betting, ...(Object.values(PAGES.BETTING))];

    return bettingPages.includes(this.currentLinkModel?.name);
  }

}
